import { useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Search, Plus, Minus, Trash2, ArrowRight, ListChecks, Box, Percent, Truck, Store, Tag, Usb, Bluetooth, BluetoothOff, Wifi, WifiOff, Unplug, CalendarDays, X } from 'lucide-react';
// import posTerminalImg from '../../assets/3d_billing_machine.png';
import { StockLocationBar } from '../../components/StockLocationBar';
import { CustomSelect } from '../../components/CustomSelect';
import { fetchWithAuth } from '../../utils/api';
import { DEFAULT_TEMPLATE_ID, resolveTemplateSettings } from '../../data/billTemplates';
import { loadBillTemplates } from '../../data/billTemplateStore';
import { connectUSB, connectBluetooth, buildReceiptData, printViaUSB, printViaBluetooth, printViaWifi, getBluetoothStatus, disconnectBluetooth, getUsbStatus, disconnectUSB, describeBluetoothError } from '../../utils/printer';
import {
  resolveStockScopes, effectiveBranchesOf, CENTRAL_LOCATION,
  stockRowsOf, findCatalogProduct, variantLabelOf, productImagesOf, findGst, splitGst,
  isInclusive, taxModeOf, taxOf, shopTaxMode, payable, roundOffOf,
  findPSubCategory, findPCategory, createBill, isModuleOn, getOrgSettings, deliveryFeesOf,
  catalogProductsOf, ORG_TYPES, catalogServicesOf, findSCategory, findSSubCategory, users,
  masterStates
} from '../../data/store';
import { billNumberOf } from '../../utils/billNumber';

// Order types in the POS Bill panel. `set-to-deliver` only appears when the
// org has the delivery module on — for online/e-commerce orders use the
// separate online-orders flow, not this in-store POS.
const ORDER_TYPE_META = {
  'in-shop': { label: 'In Shop', desc: 'Walks in, pays, leaves with goods.', icon: Store },
  'set-to-deliver': { label: 'Set to Deliver', desc: 'Paid in-store, agent delivers to address.', icon: Truck },
  'booking': { label: 'Booking', desc: 'From an appointment booking.', icon: CalendarDays },
};

// A placeholder address saved during earlier testing. It is a router address,
// not a printer, so it can never answer on port 9100 — probing it only produces
// a 3-second stall and a 504 on every poll.
const DUMMY_PRINTER_IP = '192.168.1.1';

export const BillingTab = ({ org, user, showNotification, onBillPrinted, lockedBranchId, initialPrefill, clearPrefill }) => {
  const [version, setVersion] = useState(0);
  const [btStatus, setBtStatus] = useState(false);
  const [usbStatus, setUsbStatus] = useState(false);
  const [wifiStatus, setWifiStatus] = useState(false);
  const [showWifiModal, setShowWifiModal] = useState(false);
  const [wifiIpInput, setWifiIpInput] = useState('');
  const [isTestingWifi, setIsTestingWifi] = useState(false);
  const [wifiTestError, setWifiTestError] = useState('');
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(() => sessionStorage.getItem('org.billingCategory') || null);
  const [orderType, setOrderType] = useState('in-shop');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  // Which state it is going to. Asked only here, because this is the only
  // sale where the goods leave the shop - a customer who lives elsewhere and
  // walks in is still an intra-state sale, and asking about it on every bill
  // would be asking the counter to get it wrong.
  const [deliveryState, setDeliveryState] = useState('');
  const [customer, setCustomer] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  // Which of the two customer fields to outline in red. Cleared as soon as the
  // cashier types into one, so the warning goes away while it is being fixed
  // rather than sitting there until the next attempt.
  const [customerErrors, setCustomerErrors] = useState({ name: false, phone: false });
  const [paymentMethod, setPaymentMethod] = useState(() => sessionStorage.getItem('org.billingPayment') || 'Card');
  const [deliveryFeeSelected, setDeliveryFeeSelected] = useState('none');
  const [customDeliveryFee, setCustomDeliveryFee] = useState('');
  const [cart, setCart] = useState([]);
  const [mobileView, setMobileView] = useState(() => sessionStorage.getItem('org.billingMobileView') || 'catalog');
  const [layoutMode] = useState('table');
  const [activeCodeInput, setActiveCodeInput] = useState('');
  const [activeNameInput, setActiveNameInput] = useState('');
  const [focusedMatchIndex, setFocusedMatchIndex] = useState(-1);
  const [isEditingActiveRow, setIsEditingActiveRow] = useState(true);
  const [activeStaffId, setActiveStaffId] = useState('');
  const [activeStaffInput, setActiveStaffInput] = useState('');

  // Discount and Offers State
  // eslint-disable-next-line no-unused-vars
  const [offers, setOffers] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);

  const [customers, setCustomers] = useState([]);
  const [customerMemberships, setCustomerMemberships] = useState([]);

  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        const [oRes, dRes, cRes, cmRes] = await Promise.all([
          fetchWithAuth('/offers', { headers: { 'x-org-id': org.id } }),
          fetchWithAuth('/discounts', { headers: { 'x-org-id': org.id } }),
          fetchWithAuth('/customers', { headers: { 'x-org-id': org.id } }),
          fetchWithAuth('/customermemberships', { headers: { 'x-org-id': org.id } })
        ]);
        const oData = await oRes.json();
        const dData = await dRes.json();
        const cData = await cRes.json();
        const cmData = await cmRes.json();
        setOffers(oData || []);
        setDiscounts(dData || []);
        setCustomers(cData || []);
        setCustomerMemberships(cmData || []);
      } catch (err) {
        console.error('Failed to fetch billing data', err);
      }
    };
    if (org?.id) fetchBillingData();
  }, [org?.id]);

  useEffect(() => {
    if (!activeStaffId && user && users.length > 0) {
      const me = users.find(u => u.id === user.id && u.isServiceProvider);
      if (me) {
        setActiveStaffId(me.id);
        setActiveStaffInput(me.name);
      }
    }
  }, [users, user, activeStaffId]);
  const [isStaffDropdownOpen, setIsStaffDropdownOpen] = useState(false);

  const staffOptions = useMemo(() => {
    return users.filter(u => u.orgId === org.id && (u.isServiceProvider || ['org-admin', 'branch-admin', 'super-admin'].includes(u.role)));
  }, [users, org.id]);

  const filteredStaffMatches = useMemo(() => {
    if (!activeStaffInput.trim()) return staffOptions;
    return staffOptions.filter(u => u.name.toLowerCase().includes(activeStaffInput.toLowerCase()));
  }, [staffOptions, activeStaffInput]);

  const codeInputRef = useRef(null);
  const nameInputRef = useRef(null);
  const focusTargetRef = useRef(org.type === ORG_TYPES.SERVICE ? 'name' : 'code');
  const searchCellRef = useRef(null);
  const searchDropdownRef = useRef(null);
  const staffInputRef = useRef(null);
  const staffCellRef = useRef(null);
  const staffDropdownRef = useRef(null);

  useEffect(() => {
    const positionDropdown = () => {
      const dropdownMaxHeight = 200; // roughly max-h-48 (192px) + padding
      if (searchDropdownRef.current && searchCellRef.current) {
        const rect = searchCellRef.current.getBoundingClientRect();
        searchDropdownRef.current.style.position = 'fixed';
        searchDropdownRef.current.style.left = `${rect.left}px`;
        searchDropdownRef.current.style.width = `${Math.max(rect.width, 350)}px`;
        searchDropdownRef.current.style.zIndex = '99999';
        if (rect.bottom + dropdownMaxHeight > window.innerHeight) {
          searchDropdownRef.current.style.top = 'auto';
          searchDropdownRef.current.style.bottom = `${window.innerHeight - rect.top + 4}px`;
        } else {
          searchDropdownRef.current.style.top = `${rect.bottom + 4}px`;
          searchDropdownRef.current.style.bottom = 'auto';
        }
      }
      if (staffDropdownRef.current && staffCellRef.current) {
        const rect = staffCellRef.current.getBoundingClientRect();
        staffDropdownRef.current.style.position = 'fixed';
        staffDropdownRef.current.style.left = `${rect.left}px`;
        staffDropdownRef.current.style.width = `${Math.max(rect.width, 200)}px`;
        staffDropdownRef.current.style.zIndex = '99999';
        if (rect.bottom + dropdownMaxHeight > window.innerHeight) {
          staffDropdownRef.current.style.top = 'auto';
          staffDropdownRef.current.style.bottom = `${window.innerHeight - rect.top + 4}px`;
        } else {
          staffDropdownRef.current.style.top = `${rect.bottom + 4}px`;
          staffDropdownRef.current.style.bottom = 'auto';
        }
      }
    };
    positionDropdown();
    const tableContainer = document.querySelector('.table-scrollbar');
    if (tableContainer) tableContainer.addEventListener('scroll', positionDropdown);
    window.addEventListener('resize', positionDropdown);
    return () => {
      if (tableContainer) tableContainer.removeEventListener('scroll', positionDropdown);
      window.removeEventListener('resize', positionDropdown);
    };
  });

  useEffect(() => {
    setFocusedMatchIndex(-1);
  }, [activeCodeInput, activeNameInput]);

  const proceedRef = useRef(null);
  useEffect(() => {
    proceedRef.current = proceed;
  });

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if ((e.ctrlKey && e.key.toLowerCase() === 'p') || e.key === 'F8') {
        e.preventDefault();
        proceedRef.current(true);
        return;
      }
      if (e.key.toLowerCase() === 'p') {
        const activeEl = document.activeElement;
        if (
          activeEl &&
          (activeEl.tagName === 'INPUT' ||
            activeEl.tagName === 'TEXTAREA' ||
            activeEl.isContentEditable)
        ) {
          return;
        }
        e.preventDefault();
        proceedRef.current(true);
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  const [pendingPrefill, setPendingPrefill] = useState(null);

  useEffect(() => {
    if (initialPrefill) {
      setPendingPrefill(initialPrefill);
      if (clearPrefill) clearPrefill();
    }
  }, [initialPrefill, clearPrefill]);

  useEffect(() => {
    const handleMethodChanged = () => {
      setVersion(v => v + 1); // trigger re-render
    };
    window.addEventListener('org-printer-method-changed', handleMethodChanged);
    return () => window.removeEventListener('org-printer-method-changed', handleMethodChanged);
  }, []);

  useEffect(() => {
    setBtStatus(getBluetoothStatus());
    setUsbStatus(getUsbStatus());

    // A page reload destroys the Web Bluetooth link — the GATT connection
    // belongs to the document. The browser keeps the permission, so a printer
    // paired earlier can be reconnected silently. Without this, refreshing the
    // billing screen left the printer showing disconnected until the cashier
    // either opened Printer settings or hit the failure at print time.
    // No reconnect attempt here: OrgDashboard runs a supervisor that keeps the
    // printer connected for as long as the user wants it, from whichever page
    // they happen to be on. A second attempt from this tab would only compete
    // with it for the radio. The 2-second poll below picks up the result.

    const handleConnected = () => setBtStatus(true);
    const handleDisconnected = () => setBtStatus(false);
    const handleUsbDisconnected = () => setUsbStatus(false);
    // printer.js dispatches 'org-printer-bt-*'. This file listened for
    // 'bluetooth-printer-*', which is never dispatched anywhere, so these
    // handlers had never once fired and the status relied entirely on the
    // 2-second poll below.
    window.addEventListener('org-printer-bt-connected', handleConnected);
    window.addEventListener('org-printer-bt-disconnected', handleDisconnected);
    window.addEventListener('usb-printer-disconnected', handleUsbDisconnected);

    const intv = setInterval(() => {
      setBtStatus(getBluetoothStatus());
      setUsbStatus(getUsbStatus());
    }, 2000);

    const wifiIntv = setInterval(() => {
      // Only poll when Wi-Fi is the chosen printer. This ran unconditionally,
      // so a shop printing over Bluetooth or USB still probed a Wi-Fi address
      // every 4 seconds. Each probe blocks the API for up to 3 seconds in
      // net.DialTimeout, so an unreachable address meant a permanent queue of
      // slow requests and a console full of 504s.
      if ((localStorage.getItem('preferredPrinterMethod') || '') !== 'wifi') {
        setWifiStatus(false);
        return;
      }
      const printerStr = localStorage.getItem('org.device.printer');
      const printerConf = printerStr ? JSON.parse(printerStr) : null;
      const ip = printerConf?.ip || localStorage.getItem('printerWifiIp');
      // 192.168.1.1 is a placeholder that got saved during earlier testing, and
      // is a router address rather than a printer, so it can never answer on
      // port 9100. Treat it as "not configured" instead of probing it forever.
      if (!ip || ip === DUMMY_PRINTER_IP) {
        setWifiStatus(false);
        return;
      }
      import('../../utils/api').then(({ fetchWithAuth }) => {
        fetchWithAuth(`/print/wifi/check?ip=${ip}`)
          .then(res => setWifiStatus(res.ok))
          .catch(() => setWifiStatus(false));
      });
    }, 4000); // Poll Wi-Fi every 4 seconds

    return () => {
      window.removeEventListener('org-printer-bt-connected', handleConnected);
      window.removeEventListener('org-printer-bt-disconnected', handleDisconnected);
      window.removeEventListener('usb-printer-disconnected', handleUsbDisconnected);
      clearInterval(intv);
      clearInterval(wifiIntv);
    };
  }, []);

  useEffect(() => {
    if (activeCategory) sessionStorage.setItem('org.billingCategory', activeCategory);
    else sessionStorage.removeItem('org.billingCategory');
  }, [activeCategory]);
  useEffect(() => { sessionStorage.setItem('org.billingPayment', paymentMethod); }, [paymentMethod]);
  useEffect(() => { sessionStorage.setItem('org.billingMobileView', mobileView); }, [mobileView]);
  useEffect(() => { sessionStorage.setItem('org.billingLayout', layoutMode); }, [layoutMode]);
  const layoutModeIsInitialMount = useRef(true);
  useEffect(() => {
    if (layoutModeIsInitialMount.current) {
      layoutModeIsInitialMount.current = false;
      if (layoutMode === 'table') {
        focusTargetRef.current = org.type === ORG_TYPES.SERVICE ? 'name' : 'code';
        setIsEditingActiveRow(true);
      } else {
        setIsEditingActiveRow(false);
      }
      return;
    }
    // Clear the bill whenever the user switches modes — each mode starts fresh.
    setCart([]);
    setCustomer('');
    setCustomerErrors({ name: false, phone: false });
    setCustomerPhone('');
    setDeliveryAddress('');
    setActiveCodeInput('');
    setActiveNameInput('');
    if (layoutMode === 'table') {
      focusTargetRef.current = org.type === ORG_TYPES.SERVICE ? 'name' : 'code';
      setIsEditingActiveRow(true);
    } else {
      setIsEditingActiveRow(false);
    }
  }, [layoutMode]);

  useEffect(() => {
    if (layoutMode === 'table' && isEditingActiveRow) {
      const timer = setTimeout(() => {
        const el = focusTargetRef.current === 'name' ? nameInputRef.current : codeInputRef.current;
        if (el) {
          el.focus({ preventScroll: true });
        }
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [layoutMode, isEditingActiveRow, cart.length]);

  const deliveryEnabled = isModuleOn(org, 'delivery');
  const appointmentsEnabled = isModuleOn(org, 'appointments');
  const isPrinterModuleEnabled = isModuleOn(org, 'printer');
  const orderTypeOptions = useMemo(() => {
    const opts = ['in-shop'];
    if (deliveryEnabled) opts.push('set-to-deliver');
    if (appointmentsEnabled) opts.push('booking');
    return opts;
  }, [deliveryEnabled, appointmentsEnabled]);
  // If delivery flips off mid-session, snap back to in-shop.
  useEffect(() => {
    if (!orderTypeOptions.includes(orderType)) {
      setOrderType('in-shop');
      setDeliveryAddress('');
    }
  }, [orderTypeOptions, orderType]);

  // Reset delivery fee if order type is not set-to-deliver
  useEffect(() => {
    if (orderType !== 'set-to-deliver') {
      setDeliveryFeeSelected('none');
      setCustomDeliveryFee('');
    }
  }, [orderType]);

  const scopes = useMemo(() => resolveStockScopes(org.id), [org.id]);
  const branches = useMemo(() => effectiveBranchesOf(org.id), [org.id]);

  // The receipt design this till prints with. Loaded once: a shop changes its
  // bill format rarely, and a fetch per sale would slow the checkout for no
  // reason. Presets need no fetch, so a failed load still prints correctly.
  const [billTemplates, setBillTemplates] = useState([]);
  useEffect(() => {
    let cancelled = false;
    loadBillTemplates()
      .then((list) => { if (!cancelled) setBillTemplates(list); });
    return () => { cancelled = true; };
  }, []);

  const resolveLocation = () => {
    if (scopes.inventoryScope === 'central') return CENTRAL_LOCATION;
    if (lockedBranchId) return lockedBranchId;
    return branches[0]?.id || '';
  };
  const [locationId, setLocationId] = useState(resolveLocation);
  useEffect(() => {
    setLocationId(resolveLocation());
  }, [scopes.inventoryScope, branches, lockedBranchId]);

  const inventoryEnabled = isModuleOn(org, 'inventory');

  // Sellable rows = active stock at the location, joined with catalog product + GST.
  // For Service orgs, we load services (infinite stock). For Hybrid, we load both.
  const sellable = useMemo(() => {
    let items = [];
    const isService = org.type === ORG_TYPES.SERVICE || org.type === ORG_TYPES.BOTH;
    const isProduct = org.type !== ORG_TYPES.SERVICE; // PRODUCT or BOTH

    if (isProduct) {
      if (!inventoryEnabled) {
        const defaultLoc = effectiveBranchesOf(org.id)[0]?.id || 'main';
        const activeRows = stockRowsOf(org.id, defaultLoc, 'active');
        const prodItems = catalogProductsOf(org.id).flatMap((product) => {
          const sub = findPSubCategory(product.subCategoryId);
          const cat = sub ? findPCategory(sub.categoryId) : null;
          const variants = (product.variants && product.variants.length > 0)
            ? product.variants
            : [{ id: 'base', price: product.basePrice, label: '' }];

          return variants.map(v => {
            const vId = v.id === 'base' ? '' : v.id;
            const st = activeRows.find(r => r.productId === product.id && (r.variantId || '') === vId);
            const qty = 999999;
            return {
              stockRowId: st ? st.id : `${product.id}-${v.id}`,
              productId: product.id,
              variantId: v.id === 'base' ? null : v.id,
              name: product.name,
              label: v.label || '',
              category: cat?.name || 'Other',
              price: v.price || product.basePrice,
              qty,
              gstId: product.gstId || '',
              images: productImagesOf(product, v.id === 'base' ? null : v.id),
              barcode: v.id === 'base' ? (product.barcode || '') : (v.barcode || product.barcode || ''),
              isService: false,
            };
          });
        });
        items = [...items, ...prodItems];
      } else {
        const prodItems = stockRowsOf(org.id, locationId, 'active')
          .filter((r) => r.qty > 0)
          .map((r) => {
            const product = findCatalogProduct(r.productId);
            const sub = product ? findPSubCategory(product.subCategoryId) : null;
            const cat = sub ? findPCategory(sub.categoryId) : null;
            let vBarcode = product?.barcode || '';
            if (product && r.variantId) {
              const v = (product.variants || []).find(x => x.id === r.variantId);
              if (v && v.barcode) vBarcode = v.barcode;
            }
            return {
              stockRowId: r.id,
              productId: r.productId,
              variantId: r.variantId,
              name: product?.name || r.productId,
              label: variantLabelOf(product, r.variantId),
              category: cat?.name || 'Other',
              price: r.sellPrice,
              qty: r.qty,
              batchNumber: r.batchNumber || '',
              expiryDate: r.expiryDate || '',
              gstId: product?.gstId || '',
              images: productImagesOf(product, r.variantId),
              barcode: r.barcode || vBarcode,
              isService: false,
            };
          });
        items = [...items, ...prodItems];
      }
    }

    if (isService) {
      const serviceItems = catalogServicesOf(org.id).map(srv => {
        const sub = findSSubCategory(srv.subCategoryId);
        const cat = sub ? findSCategory(sub.categoryId) : null;
        return {
          stockRowId: `srv-${srv.id}`,
          productId: srv.id,
          variantId: null,
          name: srv.name,
          label: '',
          category: cat?.name || 'Service',
          price: srv.price,
          qty: 999999, // infinite
          gstId: srv.gstId || '',
          images: [],
          barcode: '',
          isService: true,
        };
      });
      items = [...items, ...serviceItems];
    }

    return items;
  }, [org.id, org.type, locationId, version, inventoryEnabled]);

  // Barcode Scanner Listener
  useEffect(() => {
    const savedScanner = localStorage.getItem('org.device.scanner');
    if (!savedScanner) return;
    try {
      const parsed = JSON.parse(savedScanner);
      if (!parsed.enabled) return;
    } catch { return; }

    let barcodeBuffer = '';
    let lastKeyTime = Date.now();

    const handleScanner = (e) => {
      // Ignore if typing in an input
      const activeEl = document.activeElement;
      if (
        activeEl &&
        (activeEl.tagName === 'INPUT' ||
          activeEl.tagName === 'TEXTAREA' ||
          activeEl.isContentEditable)
      ) {
        return;
      }

      const now = Date.now();
      // Scanners typically type very fast (under 30ms per char)
      if (now - lastKeyTime > 50) {
        barcodeBuffer = '';
      }
      lastKeyTime = now;

      if (e.key === 'Enter') {
        if (barcodeBuffer.length > 3) { // Arbitrary minimum barcode length
          e.preventDefault();
          const code = barcodeBuffer.trim();
          barcodeBuffer = '';

          // Find items by barcode — prioritize fresh batches first (FIFO)
          const matches = sellable.filter(
            s => (s.barcode || '').toLowerCase() === code.toLowerCase()
          );

          if (matches.length > 0) {
            // Sort by expiry date ascending (FIFO: earlier expiry first)
            matches.sort((a, b) => {
              if (!a.expiryDate) return 1;
              if (!b.expiryDate) return -1;
              return new Date(a.expiryDate) - new Date(b.expiryDate);
            });

            // Find first non-expired match
            const today = new Date(new Date().toDateString());
            const freshMatch = matches.find(m => !m.expiryDate || new Date(m.expiryDate) >= today);

            if (!freshMatch) {
              const expiredOne = matches[0];
              if (showNotification) {
                showNotification(`⛔ CANNOT SELL! Batch ${expiredOne.batchNumber || ''} expired on ${expiredOne.expiryDate}!`, 'error');
              }
              return;
            }

            const match = freshMatch;
            setCart(prev => {
              const existing = prev.find(c => c.stockRowId === match.stockRowId);
              if (existing) {
                if (existing.qty >= match.qty && !match.isService) {
                  if (showNotification) showNotification(`Not enough stock for ${match.name}`, 'error');
                  return prev;
                }
                return prev.map(c => c.stockRowId === match.stockRowId ? { ...c, qty: c.qty + 1 } : c);
              }
              return [...prev, { ...match, qty: 1, available: match.qty, staffId: null }];
            });
            if (showNotification) {
              showNotification(`Scanned: ${match.name}${match.batchNumber ? ` (Batch: ${match.batchNumber})` : ''}`, 'success');
            }
          } else {
            if (showNotification) showNotification(`Barcode not found: ${code}`, 'error');
          }
        }
      } else if (e.key.length === 1) { // Normal printable character
        barcodeBuffer += e.key;
      }
    };

    window.addEventListener('keydown', handleScanner);
    return () => window.removeEventListener('keydown', handleScanner);
  }, [sellable, showNotification]);

  // Handle auto-prefill from appointment
  useEffect(() => {
    if (!pendingPrefill || sellable.length === 0) return;

    const { customerName, customerPhone, serviceId, staffId } = pendingPrefill;
    if (customerName) setCustomer(customerName);
    if (customerPhone) setCustomerPhone(customerPhone);

    if (serviceId) {
      const match = sellable.find(s => s.productId === serviceId);
      if (match) {
        setCart(prev => {
          if (prev.find(c => c.stockRowId === match.stockRowId)) return prev;
          return [...prev, { ...match, qty: 1, staffId: staffId || null }];
        });
      }
    }

    setPendingPrefill(null); // clear after processing
  }, [pendingPrefill, sellable]);

  const categories = useMemo(() => {
    const map = new Map();
    sellable.forEach((s) => {
      const cur = map.get(s.category) || { name: s.category, count: 0 };
      cur.count += 1;
      map.set(s.category, cur);
    });
    return Array.from(map.values());
  }, [sellable]);

  const filtered = sellable.filter((s) => {
    const okCat = activeCategory ? s.category === activeCategory : true;
    const okSearch = !search.trim()
      ? true
      : `${s.name} ${s.label} ${s.category}`.toLowerCase().includes(search.toLowerCase());
    return okCat && okSearch;
  });

  const addToCart = (item) => {
    // Removed the staffId requirement here so they can add the service by name first, 
    // and select the staff member from the cart row afterwards.
    if (item.qty <= 0) {
      if (showNotification) showNotification('No stock available for this item.');
      return;
    }
    if (item.expiryDate) {
      const expDate = new Date(item.expiryDate);
      const today = new Date(new Date().toDateString());
      if (expDate < today) {
        if (showNotification) {
          showNotification(`⛔ CANNOT SELL! Batch ${item.batchNumber || ''} expired on ${item.expiryDate}!`, 'error');
        }
        return;
      }
    }
    setCart((prev) => {
      const existing = prev.find((c) => c.stockRowId === item.stockRowId);
      if (existing) {
        if (existing.qty + 1 > item.qty) {
          if (showNotification) showNotification('No more stock for this item.');
          return prev;
        }
        return prev.map((c) => (c.stockRowId === item.stockRowId ? { ...c, qty: c.qty + 1 } : c));
      }
      return [...prev, { ...item, qty: 1, available: item.qty, staffId: item.isService ? activeStaffId : '' }];
    });
  };
  const inc = (id) => setCart((p) => p.map((c) => (c.stockRowId === id ? { ...c, qty: Math.min(c.qty + 1, c.available) } : c)));
  const dec = (id) => setCart((p) => p.map((c) => (c.stockRowId === id ? { ...c, qty: Math.max(1, c.qty - 1) } : c)));
  const drop = (id) => setCart((p) => p.filter((c) => c.stockRowId !== id));
  const updateCartQty = (id, newQty) => setCart((p) => p.map((c) => (c.stockRowId === id ? { ...c, qty: Math.max(1, Math.min(newQty, c.available)) } : c)));
  const updateCartStaff = (id, staffId) => setCart((p) => p.map((c) => (c.stockRowId === id ? { ...c, staffId } : c)));

  const activeMatches = useMemo(() => {
    const query = activeNameInput.trim().toLowerCase();
    if (!query) return [];
    return sellable.filter(
      (s) => (s.name || '').toLowerCase().includes(query)
    ).slice(0, 8);
  }, [activeNameInput, sellable]);

  const handleActiveInputKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setActiveCodeInput('');
      setActiveNameInput('');
      focusTargetRef.current = org.type === ORG_TYPES.SERVICE ? 'name' : 'code';
      const focusEl = org.type === ORG_TYPES.SERVICE ? nameInputRef.current : codeInputRef.current;
      focusEl?.focus();
      return;
    }
    if (activeMatches.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedMatchIndex((prev) => Math.min(prev + 1, activeMatches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedMatchIndex((prev) => Math.max(prev - 1, 0));
    }
  };

  const handleActiveCodeSubmit = (e) => {
    e.preventDefault();

    if (focusedMatchIndex >= 0 && activeMatches[focusedMatchIndex]) {
      addToCart(activeMatches[focusedMatchIndex]);
      setActiveCodeInput('');
      setActiveNameInput('');
      focusTargetRef.current = org.type === ORG_TYPES.SERVICE ? 'name' : 'code';
      setTimeout(() => (org.type === ORG_TYPES.SERVICE ? nameInputRef : codeInputRef).current?.focus(), 50);
      return;
    }

    if (activeNameInput.trim() && activeMatches.length === 1) {
      addToCart(activeMatches[0]);
      setActiveCodeInput('');
      setActiveNameInput('');
      focusTargetRef.current = org.type === ORG_TYPES.SERVICE ? 'name' : 'code';
      setTimeout(() => (org.type === ORG_TYPES.SERVICE ? nameInputRef : codeInputRef).current?.focus(), 50);
      return;
    }

    const query = (activeCodeInput.trim() || activeNameInput.trim()).toLowerCase();
    if (!query) return;

    const match = sellable.find(
      (s) => (s.productId || '').toLowerCase() === query
        || (s.name || '').toLowerCase() === query
        || (s.variantId || '').toLowerCase() === query
        || (s.barcode || '').toLowerCase() === query
    );
    if (match) {
      addToCart(match);
      setActiveCodeInput('');
      setActiveNameInput('');
      focusTargetRef.current = org.type === ORG_TYPES.SERVICE ? 'name' : 'code';
      setTimeout(() => (org.type === ORG_TYPES.SERVICE ? nameInputRef : codeInputRef).current?.focus(), 50);
    } else {
      if (showNotification) showNotification('Item code or name not found in active stock.', 'error');
    }
  };

  // GST-aware totals.
  const deliveryFeeSlabs = useMemo(() => deliveryFeesOf(org.id).slice().sort((a, b) => a.upto - b.upto), [org.id]);
  const resolvedDeliveryFee = useMemo(() => {
    if (deliveryFeeSelected === 'none') return 0;
    if (deliveryFeeSelected === 'custom') return parseFloat(customDeliveryFee) || 0;
    const slab = deliveryFeeSlabs.find(s => String(s.id) === String(deliveryFeeSelected));
    return slab ? slab.fee : 0;
  }, [deliveryFeeSelected, customDeliveryFee, deliveryFeeSlabs]);

  const handleApplyPromo = () => {
    const code = promoCodeInput.trim().toLowerCase();
    if (!code) return;

    // In the backend, name is used for code right now for basic discount
    const validDiscount = discounts.find(d =>
      (d.name || d.code || '').toLowerCase() === code &&
      (!d.status || d.status === 'active')
    );

    if (validDiscount) {
      if (validDiscount.validUntil && new Date(validDiscount.validUntil) < new Date()) {
        if (showNotification) showNotification('This promo code has expired.', 'error');
        return;
      }
      setAppliedDiscount(validDiscount);
      if (showNotification) showNotification('Promo code applied successfully!', 'success');
    } else {
      setAppliedDiscount(null);
      if (showNotification) showNotification('Invalid promo code.', 'error');
    }
  };

  // Whether a price contains its GST or is charged on top of it is the
  // line's tax mode: the shop's setting, or the product's own where it states
  // one. The same split the checkout handler applies, so the total on screen
  // is the total on the bill.
  //
  // `subtotal` stays the taxable base either way, so subtotal + tax is what
  // is due. On an inclusive basket that makes the subtotal smaller than the
  // prices in the cart, which is correct and is what a tax-inclusive bill has
  // always looked like.
  const totals = useMemo(() => {
    let subtotal = 0; let cgst = 0; let sgst = 0; let igst = 0;
    // True only if every line is inclusive. A basket mixing MRP goods with
    // loose produce is neither, and is labelled as neither.
    let inclusive = cart.length > 0;
    cart.forEach((c) => {
      const gst = findGst(c.gstId);
      const rate = gst ? gst.rate : 0;
      const split = splitGst(rate, false);
      const lineInclusive = isInclusive(
        taxModeOf(findCatalogProduct(c.productId) || c, shopTaxMode()),
      );
      if (!lineInclusive) inclusive = false;
      const cut = taxOf(c.qty * c.price, rate, lineInclusive);
      subtotal += cut.base;
      // Halved from the tax rather than worked out twice from the base, so
      // CGST + SGST is the tax exactly.
      if (split.igst) {
        igst += cut.tax;
      } else {
        const half = Math.round((cut.tax / 2) * 100) / 100;
        cgst += half;
        sgst += Math.round((cut.tax - half) * 100) / 100;
      }
    });

    let discountAmount = 0;
    if (appliedDiscount) {
      if (appliedDiscount.type === 'percentage') {
        discountAmount = (subtotal * appliedDiscount.value) / 100;
      } else {
        discountAmount = appliedDiscount.value;
      }
      if (discountAmount > subtotal) discountAmount = subtotal;
    }

    const round = (n) => +n.toFixed(2);
    // What is due, and then what is taken. Nobody pays 143.80: the bill is
    // charged at the nearest rupee and the paise are shown on their own line
    // rather than folded into the tax, which stays exactly what was charged.
    const due = round(Math.max(0, subtotal - discountAmount + cgst + sgst + igst + resolvedDeliveryFee));
    return {
      inclusive,
      subtotal: round(subtotal),
      cgst: round(cgst),
      sgst: round(sgst),
      igst: round(igst),
      discount: round(discountAmount),
      deliveryFee: round(resolvedDeliveryFee),
      due,
      roundOff: roundOffOf(due),
      total: payable(due),
    };
  }, [cart, resolvedDeliveryFee, appliedDiscount]);

  const accent = org.theme.primary;

  async function proceed(isDirectPrint) {
    if (cart.length === 0) {
      if (showNotification) showNotification('Add at least one item before billing.');
      return;
    }

    // Customer name and number are optional.
    // If a phone number is entered, validate that it has 10 digits.
    const phoneDigits = customerPhone.replace(/\D/g, '');
    if (phoneDigits && phoneDigits.length < 10) {
      setCustomerErrors({ name: false, phone: true });
      if (showNotification) showNotification('The customer number must be 10 digits.', 'error');
      return;
    }

    setCustomerErrors({ name: false, phone: false });
    if (orderType === 'set-to-deliver' && !deliveryAddress.trim()) {
      if (showNotification) showNotification('Enter a delivery address.', 'error');
      return;
    }

    const actualIsDirectPrint = isDirectPrint && isPrinterModuleEnabled;
    const printMethod = isPrinterModuleEnabled ? (localStorage.getItem('preferredPrinterMethod') || 'browser') : 'browser';

    // PRE-FLIGHT CHECKS: Prevent clearing cart and generating bill if printer is disconnected
    if (actualIsDirectPrint === true) {
      if (printMethod === 'bluetooth' && !btStatus) {
        let reconnected = false;
        try {
          const { autoReconnectBluetooth, connectBluetooth } = await import('../../utils/printer');
          reconnected = await autoReconnectBluetooth();
          if (!reconnected) {
            // Checkout button press is a valid user gesture: attempt interactive connect if silent reconnect fails
            await connectBluetooth();
            reconnected = true;
          }
        } catch(e) {}
        if (!reconnected) {
          if (showNotification) showNotification('Bluetooth printer is not connected. Check it is on and nearby, then reconnect it from Devices → Printer.', 'error');
          return;
        }
      }
      if (printMethod === 'usb' && !usbStatus) {
        if (showNotification) showNotification('USB printer is disconnected! Please connect it first.', 'error');
        return;
      }
      if (printMethod === 'wifi') {
        const printerStr = localStorage.getItem('org.device.printer');
        const printerConf = printerStr ? JSON.parse(printerStr) : null;
        const ip = printerConf?.ip || localStorage.getItem('printerWifiIp');
        if (!ip) {
          if (showNotification) showNotification('Wi-Fi printer IP is not set! Configure it in Devices.', 'error');
          return;
        }
        if (!wifiStatus) {
          if (showNotification) showNotification('Wi-Fi printer is disconnected! Please ensure it is turned on.', 'error');
          return;
        }
      }
    }

    const bill = createBill({
      orgId: org.id,
      locationId,
      userId: user?.id || null,
      customer: customer.trim() || 'Walk-in',
      customerPhone: customerPhone.trim(),
      method: paymentMethod,
      orderType,
      deliveryAddress: orderType === 'set-to-deliver' ? deliveryAddress.trim() : '',
      deliveryState: orderType === 'set-to-deliver' ? deliveryState : '',
      deliveryFee: totals.deliveryFee,
      discountId: appliedDiscount ? appliedDiscount.id : null,
      discountAmount: totals.discount,
      items: cart.map((c) => ({
        stockRowId: c.stockRowId,
        productId: c.productId, variantId: c.variantId, name: c.name, label: c.label,
        price: c.price, qty: c.qty, gstId: c.gstId, staffId: c.staffId, isService: c.isService,
        batchNumber: c.batchNumber || '', expiryDate: c.expiryDate || '',
      })),
    });
    // Nothing goes in front of a customer until the server has issued the real
    // bill number. Printing used to start the moment createBill() returned, so
    // the receipt carried the local `bill-temp-…` placeholder — and a sale the
    // server rejected was still printed and handed over.
    const recorded = await bill.ready;
    if (!recorded) {
      if (showNotification) showNotification('The sale was not recorded, so nothing was printed. Try again.', 'error');
      return;
    }

    if (actualIsDirectPrint === true && printMethod === 'browser') {
      if (showNotification) showNotification(`Bill ${billNumberOf(bill)} generated. Preparing print...`, 'success');
      if (onBillPrinted) onBillPrinted(bill); // Triggers PrintBillModal which auto-prints
    } else if (actualIsDirectPrint === true && (printMethod === 'usb' || printMethod === 'bluetooth' || printMethod === 'wifi')) {
      try {
        // Each branch can print a different design; an unknown or deleted id
        // falls back to the standard preset rather than failing the sale.
        const branch = branches.find((b) => b.id === (lockedBranchId || bill.locationId));
        const templateId = branch?.billTemplateId || DEFAULT_TEMPLATE_ID;
        const data = buildReceiptData(org, bill, resolveTemplateSettings(templateId, billTemplates));
        if (printMethod === 'usb') await printViaUSB(data);
        if (printMethod === 'bluetooth') await printViaBluetooth(data);
        if (printMethod === 'wifi') {
          const printerStr = localStorage.getItem('org.device.printer');
          const printerConf = printerStr ? JSON.parse(printerStr) : null;
          const ip = printerConf?.ip || localStorage.getItem('printerWifiIp');
          if (!ip) throw new Error('Wi-Fi printer IP not set. Please configure it in the Devices section.');
          await printViaWifi(data, ip);
        }
        if (showNotification) showNotification('Print successful!', 'success');
        if (onBillPrinted) onBillPrinted(bill);
      } catch (e) {
        console.error(e);
        // Raw Web Bluetooth messages — and internal ones like IGNORE_LOCK —
        // told the cashier nothing they could act on. Say what to do instead.
        const reason = printMethod === 'bluetooth' ? describeBluetoothError(e) : e.message;
        if (showNotification) showNotification('Bill saved, but printing failed: ' + reason, 'error');
        if (onBillPrinted) onBillPrinted(bill);
      }
    } else {
      if (showNotification) showNotification(`Bill ${billNumberOf(bill)} generated.`, 'success');
      if (onBillPrinted) onBillPrinted(bill);
    }

    setCart([]);
    setCustomer('');
    setCustomerErrors({ name: false, phone: false });
    setCustomerPhone('');
    setDeliveryAddress('');
    setDeliveryFeeSelected('none');
    setCustomDeliveryFee('');
    setVersion((v) => v + 1);
  };

  // Match StockLocationBar's own visibility check so we don't render an
  // empty white container when there's no warehouse/multi-branch context.
  const showLocationBar = isModuleOn(org, 'warehouse') || isModuleOn(org, 'branches') || isModuleOn(org, 'zone');

  return (
    <div className="flex-1 flex flex-col h-full min-h-full min-h-0 gap-4 w-full">
      <section className="shrink-0 w-full">
        <div className="flex items-center overflow-x-auto scrollbar-hide w-full py-1">
          {layoutMode === 'table' ? (
            <>
              <div className="flex items-center gap-2 shrink-0">
                {showLocationBar && (
                  <div className="pr-1">
                    <StockLocationBar
                      org={org}
                      scope={scopes.inventoryScope}
                      mode={scopes.mode}
                      branches={branches}
                      locationId={locationId}
                      onChange={setLocationId}
                      accent={accent}
                      lockedBranchId={lockedBranchId}
                    />
                  </div>
                )}

              </div>
              <div className="hidden sm:block flex-1" />
              <div className="flex flex-col items-end gap-1.5 shrink-0 px-2">
                {console.log('--- BILLING TAB RENDER --- Printer Module is:', isModuleOn(org, 'printer'))}

                {isModuleOn(org, 'printer') && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 rounded-lg">

                    <button
                      type="button"
                      onClick={async () => {
                        const currentMethod = localStorage.getItem('preferredPrinterMethod') || 'browser';
                        if (currentMethod !== 'usb') {
                          localStorage.setItem('preferredPrinterMethod', 'usb');
                          window.dispatchEvent(new CustomEvent('org-printer-method-changed'));
                          if (showNotification) showNotification('Active printer set to USB', 'success');
                        } else {
                          if (usbStatus) {
                            await disconnectUSB();
                            setUsbStatus(false);
                            if (showNotification) showNotification('USB printer disconnected', 'success');
                          } else {
                            try {
                              await connectUSB();
                              setUsbStatus(true);
                            } catch (err) {
                              if (err.message !== 'IGNORE_LOCK' &&
                                !err.message.includes('No printer was selected') &&
                                !err.message.includes('User cancelled') &&
                                showNotification) {
                                showNotification('USB connection failed: ' + err.message, 'error');
                              }
                            }
                          }
                        }
                      }}
                      className={`px-3 py-1 text-[10px] font-bold uppercase rounded transition-colors flex items-center gap-1 ${(localStorage.getItem('preferredPrinterMethod') || 'browser') === 'usb'
                          ? (usbStatus ? 'bg-emerald-600 text-white shadow-sm' : 'bg-red-600 text-white shadow-sm')
                          : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                      {usbStatus ? <Usb size={12} /> : <Unplug size={12} />} USB
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        const currentMethod = localStorage.getItem('preferredPrinterMethod') || 'browser';
                        if (currentMethod !== 'bluetooth') {
                          localStorage.setItem('preferredPrinterMethod', 'bluetooth');
                          window.dispatchEvent(new CustomEvent('org-printer-method-changed'));
                          if (showNotification) showNotification('Active printer set to Bluetooth', 'success');
                        } else {
                          if (btStatus) {
                            await disconnectBluetooth();
                            setBtStatus(false);
                            if (showNotification) showNotification('Bluetooth printer disconnected', 'success');
                          } else {
                            try {
                              await connectBluetooth();
                              setBtStatus(true);
                            } catch (err) {
                              if (err.message !== 'IGNORE_LOCK' &&
                                !err.message.includes('User cancelled') &&
                                showNotification) {
                                showNotification('Bluetooth connection failed: ' + err.message, 'error');
                              }
                            }
                          }
                        }
                      }}
                      className={`px-3 py-1 text-[10px] font-bold uppercase rounded transition-colors flex items-center gap-1 ${(localStorage.getItem('preferredPrinterMethod') || 'browser') === 'bluetooth'
                          ? (btStatus ? 'bg-emerald-600 text-white shadow-sm' : 'bg-red-600 text-white shadow-sm')
                          : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                      {btStatus ? <Bluetooth size={12} /> : <BluetoothOff size={12} />} Bluetooth
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const currentMethod = localStorage.getItem('preferredPrinterMethod') || 'browser';
                        const printerStr = localStorage.getItem('org.device.printer');
                        const printerConf = printerStr ? JSON.parse(printerStr) : null;
                        let savedIp = printerConf?.ip || localStorage.getItem('printerWifiIp') || '';

                        // Discard the placeholder address if an earlier test saved
                        // it. This previously cleared only 'printerWifiIp', leaving
                        // the same value inside 'org.device.printer', which is read
                        // first — so it came straight back and the status poll kept
                        // probing an address that can never answer.
                        if (savedIp === DUMMY_PRINTER_IP) {
                          savedIp = '';
                          localStorage.removeItem('printerWifiIp');
                          if (printerConf) {
                            delete printerConf.ip;
                            localStorage.setItem('org.device.printer', JSON.stringify(printerConf));
                          }
                        }

                        if (currentMethod !== 'wifi') {
                          // Activating Wi-Fi only switches the method. It must not
                          // open the settings dialog: selecting a printer and
                          // configuring it are separate intentions, and popping the
                          // dialog on every activation interrupts the cashier.
                          localStorage.setItem('preferredPrinterMethod', 'wifi');
                          window.dispatchEvent(new CustomEvent('org-printer-method-changed'));
                          if (showNotification) showNotification('Active printer set to Wi-Fi', 'success');
                        } else {
                          // Already active — this click is a deliberate request to
                          // open the settings, pre-filled with the saved IP.
                          setWifiIpInput(savedIp);
                          setWifiTestError('');
                          setShowWifiModal(true);
                        }
                      }}
                      className={`px-3 py-1 text-[10px] font-bold uppercase rounded transition-colors flex items-center gap-1 ${(localStorage.getItem('preferredPrinterMethod') || 'browser') === 'wifi'
                          ? (wifiStatus ? 'bg-emerald-600 text-white shadow-sm' : 'bg-red-600 text-white shadow-sm')
                          : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                      {wifiStatus ? <Wifi size={12} /> : <WifiOff size={12} />} Wi-Fi
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="relative flex-1 min-w-[200px] ml-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} aria-hidden="true" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search active stock…"
                  aria-label="Search active inventory"
                  className="w-full bg-transparent py-1.5 pl-9 pr-4 text-xs font-medium text-slate-700 placeholder:text-slate-400 outline-none"
                />
              </div>

              {showLocationBar && <div className="w-px h-5 bg-gray-200 mx-2 hidden sm:block shrink-0" />}

              {showLocationBar && (
                <div className="shrink-0 px-1">
                  <StockLocationBar
                    org={org}
                    scope={scopes.inventoryScope}
                    mode={scopes.mode}
                    branches={branches}
                    locationId={locationId}
                    onChange={setLocationId}
                    accent={accent}
                    lockedBranchId={lockedBranchId}
                  />
                </div>
              )}

              {showLocationBar && <div className="w-px h-5 bg-gray-200 mx-2 hidden sm:block shrink-0" />}
            </>
          )}

          {/* Visual POS / Direct Entry toggles removed per log.md */}
        </div>
      </section>

      <div className="flex-1 min-h-0 flex flex-col md:flex-row gap-4 md:gap-5 relative">
        <section className={`flex-1 min-h-0 flex-col gap-4 overflow-hidden ${mobileView === 'catalog' ? 'flex' : 'hidden md:flex'}`}>
          {layoutMode === 'card' ? (
            <>
              {categories.length > 0 && (
                <div className="flex items-center gap-3 shrink-0 overflow-x-auto no-scrollbar pb-2 pt-1 -mx-1 px-1">
                  <button
                    type="button"
                    onClick={() => setActiveCategory(null)}
                    className="shrink-0 flex flex-col items-center justify-center gap-1.5 w-[76px] h-[76px] rounded-[18px] transition-all hover:-translate-y-0.5"
                    style={activeCategory === null
                      ? { background: '#fff', border: `2px solid ${accent}`, color: accent }
                      : { background: '#fff', border: '1px solid var(--theme-tertiary)', color: '#64748b' }}
                  >
                    <img src="/icons/icon_all.png" alt="All Categories" className="w-[38px] h-[38px] object-contain mix-blend-multiply brightness-110 contrast-125" />
                    <span className="text-[11px] font-bold">All</span>
                  </button>
                  {categories.map((cat) => {
                    const isActive = activeCategory === cat.name;
                    const n = cat.name.toLowerCase();
                    let emoji = '📦';
                    let customImg = null;

                    if (n.includes('wine') || n.includes('bar') || n.includes('drink') || n.includes('liquor')) emoji = '🍷';
                    else if (n.includes('pizza')) emoji = '🍕';
                    else if (n.includes('soup')) emoji = '🥣';
                    else if (n.includes('fish') || n.includes('seafood')) emoji = '🐟';
                    else if (n.includes('burger')) emoji = '🍔';
                    else if (n.includes('meat') || n.includes('steak')) emoji = '🥩';
                    else if (n.includes('dessert') || n.includes('sweet') || n.includes('cake')) emoji = '🍰';
                    else if (n.includes('salad') || n.includes('veg')) emoji = '🥗';
                    else if (n.includes('coffee') || n.includes('cafe')) emoji = '☕';
                    else if (n.includes('food') || n.includes('meal')) emoji = '🥘';
                    else if (n.includes('groc') || n.includes('market') || n === 'g' || n.includes('supermarket')) customImg = '/icons/icon_grocery.png';
                    else if (n.includes('electron') || n.includes('tech') || n.includes('mobile') || n.includes('computer')) customImg = '/icons/icon_electronics.png';
                    else if (n.includes('cloth') || n.includes('apparel') || n.includes('fashion') || n.includes('wear')) customImg = '/icons/icon_cloth.png';
                    else if (n.includes('snack') || n.includes('chips') || n.includes('biscuit') || n === 's') customImg = '/icons/icon_snacks.png';
                    else if (n.includes('house') || n.includes('home') || n.includes('clean') || n === 'h') customImg = '/icons/icon_household.png';
                    else if (n.includes('shoe') || n.includes('footwear')) emoji = '👟';
                    else if (n.includes('book') || n.includes('stationery')) emoji = '📚';
                    else if (n.includes('beauty') || n.includes('cosmetic') || n.includes('makeup')) emoji = '💄';
                    else if (n.includes('health') || n.includes('pharmacy') || n.includes('medical')) emoji = '💊';
                    else if (n.includes('furniture') || n.includes('decor')) emoji = '🛋️';
                    else if (n.includes('toy') || n.includes('game') || n.includes('kids')) emoji = '🧸';
                    else if (n.includes('sport') || n.includes('fitness')) emoji = '⚽';
                    else if (n.includes('gift') || n.includes('present')) emoji = '🎁';
                    else if (n.includes('pet') || n.includes('animal')) emoji = '🐾';
                    else if (n.includes('jewel') || n.includes('accessory')) emoji = '💎';

                    return (
                      <button
                        key={cat.name}
                        type="button"
                        onClick={() => setActiveCategory(isActive ? null : cat.name)}
                        className="shrink-0 flex flex-col items-center justify-center gap-1.5 w-[76px] h-[76px] rounded-[18px] transition-all hover:-translate-y-0.5"
                        style={isActive
                          ? { background: '#fff', border: `2px solid ${accent}`, color: accent }
                          : { background: '#fff', border: '1px solid var(--theme-tertiary)', color: '#64748b' }}
                      >
                        {customImg ? (
                          <img src={customImg} alt={cat.name} className="w-[38px] h-[38px] object-contain mix-blend-multiply brightness-110 contrast-125" />
                        ) : (
                          <span className="text-3xl drop-shadow-sm leading-none" aria-hidden="true">{emoji}</span>
                        )}
                        <span className="text-[11px] font-bold text-[#0d0d0d] truncate max-w-[64px]">{cat.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-1">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {filtered.map((item) => (
                    <article
                      key={item.stockRowId}
                      className="group bg-white border border-[var(--theme-tertiary)] rounded-lg p-4 flex flex-col hover:border-[var(--theme-primary-tint)] hover:-translate-y-0.5 transition-all shadow-sm"
                    >
                      <div className="aspect-square w-full rounded-lg flex items-center justify-center mb-3 overflow-hidden relative" style={{ background: item.isService ? `${accent}12` : 'var(--theme-secondary)' }}>
                        {item.images?.length > 0 ? (
                          <img src={item.images[0]} alt={item.name} className="w-full h-full object-cover" />
                        ) : item.isService ? (
                          <span className="text-5xl select-none" aria-hidden="true">✂️</span>
                        ) : (
                          <Box size={42} strokeWidth={1.4} style={{ color: accent }} aria-hidden="true" />
                        )}
                        {item.isService && (
                          <span className="absolute top-1.5 right-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-white px-1.5 py-0.5 rounded" style={{ background: accent }}>
                            Service
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-auto">
                        <div className="min-w-0">
                          <h5 className="text-[13px] font-bold text-[#0d0d0d] truncate">{item.name}</h5>
                          {(() => {
                            const inCart = cart.find(c => c.stockRowId === item.stockRowId)?.qty || 0;
                            const rem = Math.max(0, item.qty - inCart);
                            // For services: show duration only. For products: show label + stock if inventory on
                            const stockInfo = item.isService ? '' : (inventoryEnabled ? ` · ${rem} left` : '');
                            return <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400 mt-0.5 truncate">{item.label}{stockInfo}</p>;
                          })()}
                          <p className="text-[11px] font-extrabold mt-0.5" style={{ color: accent }}>₹{item.price.toFixed(2)}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => addToCart(item)}
                          aria-label={`Add ${item.name}`}
                          className="shrink-0 w-9 h-9 rounded-full border transition-all flex items-center justify-center"
                          style={{ borderColor: `${accent}33`, color: accent }}
                        >
                          <Plus size={16} aria-hidden="true" />
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
                {filtered.length === 0 && (
                  <div className="py-16 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">
                    {sellable.length === 0 ? 'No active stock here — commit pending inventory first.' : 'No items match your search.'}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 min-h-0 flex flex-col bg-white rounded-lg border border-[var(--theme-tertiary)] shadow-sm font-sans text-xs overflow-hidden">
              <div className="bg-[var(--theme-secondary)] border-b border-[var(--theme-tertiary)] px-4 py-3 flex flex-nowrap items-center justify-between shrink-0 relative z-20 overflow-x-auto scrollbar-hide gap-4">
                <div className="flex flex-nowrap items-center gap-2 flex-1 min-w-0">
                  <div className="flex flex-col gap-1 shrink-0">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Date</span>
                    <span className="flex items-center justify-center px-2 py-1 bg-white border border-[var(--theme-tertiary)] rounded text-[11px] font-bold text-slate-700 shadow-sm min-w-[80px] text-center h-[30px]">
                      {new Date().toLocaleDateString('en-GB')}
                    </span>
                  </div>

                  <div className="flex flex-col gap-1 shrink-0">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Bill No</span>
                    <span className="flex items-center justify-center px-2 py-1 bg-white border border-[var(--theme-tertiary)] rounded text-[11px] font-bold text-slate-900 shadow-sm min-w-[64px] text-center h-[30px]">
                      DRAFT
                    </span>
                  </div>

                  <div className="flex flex-col gap-1 shrink-0">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                      Customer Name
                    </span>
                    <input
                      type="text"
                      value={customer}
                      onChange={e => {
                        setCustomer(e.target.value);
                        if (customerErrors.name) setCustomerErrors((prev) => ({ ...prev, name: false }));
                      }}
                      aria-invalid={customerErrors.name}
                      placeholder="Customer name"
                      className={`w-40 px-2 py-1 border rounded bg-white text-[11px] font-medium text-slate-700 focus:outline-none transition-colors shadow-sm h-[30px] ${
                        customerErrors.name
                          ? 'border-rose-500 bg-rose-50 focus:border-rose-500'
                          : 'border-[var(--theme-tertiary)] focus:border-[var(--theme-primary)]'
                      }`}
                    />
                  </div>

                  <div className="flex flex-col gap-1 shrink-0">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                      Customer No.
                    </span>
                    <input
                      type="tel"
                      aria-invalid={customerErrors.phone}
                      value={customerPhone}
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '');
                        if (val.length <= 10) {
                          if (customerErrors.phone) setCustomerErrors((prev) => ({ ...prev, phone: false }));
                          setCustomerPhone(val);
                          const matchedCust = customers.find(c => c.phone === val);
                          if (matchedCust) {
                            setCustomer(matchedCust.name);
                            const activeMem = customerMemberships.find(m => (m.customerId || '').trim() === (matchedCust.id || '').trim() && m.status === 'active');
                            if (activeMem && activeMem.plan && activeMem.plan.discountPercent > 0) {
                              setAppliedDiscount({
                                id: activeMem.plan.id,
                                type: 'percentage',
                                value: activeMem.plan.discountPercent,
                                name: `👑 ${activeMem.plan.name} (${activeMem.plan.discountPercent}%)`,
                                code: 'MEMBERSHIP'
                              });
                            }
                          }
                        }
                      }}
                      placeholder="9999999999"
                      className={`w-32 px-2 py-1 border rounded bg-white text-[11px] font-medium text-slate-700 focus:outline-none transition-colors shadow-sm h-[30px] ${
                        customerErrors.phone
                          ? 'border-rose-500 bg-rose-50 focus:border-rose-500'
                          : 'border-[var(--theme-tertiary)] focus:border-[var(--theme-primary)]'
                      }`}
                    />
                  </div>

                  {orderType === 'set-to-deliver' && (
                    <div className="flex flex-col gap-1 shrink-0">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Delivery Address</span>
                      <input
                        type="text"
                        value={deliveryAddress}
                        onChange={e => setDeliveryAddress(e.target.value)}
                        placeholder="Street, area, landmark, pincode…"
                        className="w-48 px-2 py-1 border border-[var(--theme-tertiary)] rounded bg-white text-[11px] font-medium text-slate-700 focus:border-[var(--theme-primary)] focus:outline-none transition-colors shadow-sm h-[30px]"
                      />
                    </div>
                  )}

                  {/* The state decides the tax split: delivered inside this
                      branch's state it is CGST + SGST, outside it is IGST.
                      Left blank it is treated as this state, which is what
                      every bill did before. */}
                  {orderType === 'set-to-deliver' && (
                    <div className="flex flex-col gap-1 shrink-0">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Delivery State</span>
                      <CustomSelect
                        value={deliveryState}
                        onChange={(v) => setDeliveryState(v)}
                        placeholder="This state"
                        options={masterStates.filter((s) => s.status === 'Active').map((s) => ({ value: s.name, label: s.name }))}
                        className="w-40 border-[var(--theme-tertiary)] bg-white text-[11px] font-medium normal-case tracking-normal"
                      />
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-4 ml-auto">
                  <div className="flex flex-col items-end justify-center shrink-0 bg-white border border-[var(--theme-tertiary)] rounded px-4 py-1.5 shadow-sm">
                    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-0.5">Net Total</span>
                    <span className="text-xl font-bold text-[var(--theme-primary)] leading-none font-jakarta tracking-tight">
                      ₹{totals.total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <div
                className="flex-1 min-h-0 overflow-auto custom-scrollbar table-scrollbar"
                style={{ background: 'linear-gradient(to bottom, var(--theme-secondary) 0px, var(--theme-secondary) 38px, var(--theme-tertiary) 38px, var(--theme-tertiary) 39px, white 39px, white 100%)' }}
              >
                <table className="w-full min-w-[760px] text-left border-collapse table-fixed">
                  <thead className="sticky top-0 z-[60] shadow-sm ring-1 ring-[var(--theme-tertiary)] bg-[var(--theme-secondary)]">
                    <tr className="bg-[var(--theme-secondary)] text-slate-500 select-none">
                      <th className="px-2 py-3 border-r border-[var(--theme-tertiary)] font-bold w-10 text-center text-[10px] uppercase tracking-[0.12em]">S.No</th>
                      {org.type !== ORG_TYPES.SERVICE && (
                        <th className="px-2 py-3 border-r border-[var(--theme-tertiary)] font-bold w-40 text-[10px] uppercase tracking-[0.12em] whitespace-nowrap">ITEM CODE / BARCODE</th>
                      )}
                      <th className="px-3 py-3 border-r border-[var(--theme-tertiary)] font-bold text-[10px] uppercase tracking-[0.12em]">{org.type === ORG_TYPES.SERVICE ? 'Service Name' : 'Item Name'}</th>
                      {org.type !== ORG_TYPES.PRODUCT && (
                        <th className="px-2 py-3 border-r border-[var(--theme-tertiary)] font-bold w-40 text-[10px] uppercase tracking-[0.12em]">STAFF</th>
                      )}
                      <th className="px-2 py-3 border-r border-[var(--theme-tertiary)] font-bold w-20 text-center text-[10px] uppercase tracking-[0.12em]">{org.type === ORG_TYPES.PRODUCT ? 'QTY' : 'SESSIONS'}</th>
                      <th className="px-2 py-3 border-r border-[var(--theme-tertiary)] font-bold w-20 text-right text-[10px] uppercase tracking-[0.12em]">RATE</th>
                      <th className="px-2 py-3 border-r border-[var(--theme-tertiary)] font-bold w-20 text-right text-[10px] uppercase tracking-[0.12em]">CGST</th>
                      <th className="px-2 py-3 border-r border-[var(--theme-tertiary)] font-bold w-20 text-right text-[10px] uppercase tracking-[0.12em]">SGST</th>
                      <th className="px-2 py-3 border-r border-[var(--theme-tertiary)] font-bold w-20 text-right text-[10px] uppercase tracking-[0.12em]">IGST</th>
                      <th className="px-3 py-3 border-r border-[var(--theme-tertiary)] font-bold w-24 text-right text-[10px] uppercase tracking-[0.12em]">AMOUNT</th>
                      <th className="px-2 py-3 w-10 text-center text-[10px] uppercase tracking-[0.12em]">DEL</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-700 font-medium">
                    {cart.map((c, idx) => {
                      const gst = findGst(c.gstId);
                      const rate = gst ? gst.rate : 0;
                      const split = splitGst(rate, false);
                      const base = c.qty * c.price;
                      const itemCgst = base * split.cgst / 100;
                      const itemSgst = base * split.sgst / 100;
                      const itemIgst = base * split.igst / 100;
                      const itemTotal = base + itemCgst + itemSgst + itemIgst;
                      return (
                        <tr key={c.stockRowId} className="border-b border-slate-200 hover:bg-slate-50/50 transition-colors">
                          <td className="px-2 py-2.5 border-r border-slate-200 text-center text-slate-400 font-semibold">{idx + 1}</td>
                          {org.type !== ORG_TYPES.SERVICE && (
                            <td className="px-2 py-2.5 border-r border-slate-200 font-semibold text-slate-600 text-xs" title={`ID: ${c.productId}`}>{c.barcode || c.productId}</td>
                          )}
                          <td className="px-3 py-2.5 border-r border-slate-200 truncate font-bold text-slate-800 text-xs" title={`${c.name}${c.label && c.label !== c.name ? ` - ${c.label}` : ''}`}>
                            {c.name}
                            {c.label && c.label !== c.name && (
                              <span className="font-medium text-slate-500 ml-1">
                                - {c.label}
                              </span>
                            )}
                            {(c.batchNumber || c.expiryDate) && (
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 font-normal text-[9px] leading-tight">
                                {c.batchNumber && (
                                  <span className="text-amber-700 font-mono font-medium">Batch: {c.batchNumber}</span>
                                )}
                                {c.expiryDate && (
                                  <span className="text-emerald-700 font-medium">Exp: {c.expiryDate}</span>
                                )}
                              </div>
                            )}
                          </td>
                          {org.type !== ORG_TYPES.PRODUCT && (
                            <td className="p-0 border-r border-slate-200 align-middle">
                              {c.isService ? (
                                <CustomSelect
                                  value={c.staffId || ''}
                                  onChange={(val) => updateCartStaff(c.stockRowId, val)}
                                  options={[
                                    { value: '', label: 'None' },
                                    ...users.filter(u => u.orgId === org.id && u.isServiceProvider).map(u => ({ value: u.id, label: u.name }))
                                  ]}
                                  placeholder="Select Staff..."
                                  className="w-full h-full px-2 py-3 bg-transparent border-none border-0 outline-none focus:ring-0 text-xs font-bold text-slate-900 placeholder:text-slate-400 shadow-none normal-case tracking-normal rounded-none hover:bg-transparent"
                                />
                              ) : (
                                <span className="block px-3 py-2.5 text-[10px] text-slate-400 italic">N/A</span>
                              )}
                            </td>
                          )}
                          <td className="p-0 border-r border-slate-200 align-middle">
                            <input
                              type="number"
                              min="1"
                              max={c.available}
                              value={c.qty}
                              onChange={e => updateCartQty(c.stockRowId, parseInt(e.target.value) || 1)}
                              className="w-full h-full px-2 py-3 bg-transparent border-0 text-center font-bold text-slate-800 text-xs outline-none focus:ring-0 hover:bg-slate-50/50 transition-colors"
                            />
                          </td>
                          <td className="px-2 py-2.5 border-r border-slate-200 text-right font-bold text-slate-600 text-xs">₹{c.price.toFixed(2)}</td>
                          <td className="px-2 py-2.5 border-r border-slate-200 text-right font-bold text-slate-500 text-xs">₹{itemCgst.toFixed(2)}</td>
                          <td className="px-2 py-2.5 border-r border-slate-200 text-right font-bold text-slate-500 text-xs">₹{itemSgst.toFixed(2)}</td>
                          <td className="px-2 py-2.5 border-r border-slate-200 text-right font-bold text-slate-500 text-xs">₹{itemIgst.toFixed(2)}</td>
                          <td className="px-3 py-2.5 border-r border-slate-200 text-right font-extrabold text-slate-800 text-xs">₹{itemTotal.toFixed(2)}</td>
                          <td className="px-2 py-2.5 text-center">
                            <button
                              type="button"
                              onClick={() => drop(c.stockRowId)}
                              className="p-1 rounded text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors inline-flex items-center justify-center"
                              aria-label={`Delete ${c.name}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}

                    <tr className="border-b border-slate-200 relative z-50" style={{ backgroundColor: `${accent}08` }}>
                      <td
                        className="px-2 py-3 border-r border-slate-200 text-center font-bold text-slate-700 cursor-pointer"
                        onClick={() => {
                          focusTargetRef.current = org.type === ORG_TYPES.SERVICE ? 'name' : 'code';
                          setIsEditingActiveRow(true);
                        }}
                      >
                        {isEditingActiveRow ? cart.length + 1 : ''}
                      </td>

                      {/* Item Code Cell */}
                      {org.type !== ORG_TYPES.SERVICE && (
                        isEditingActiveRow ? (
                          <td className="p-0 border-r border-slate-200 align-middle">
                            <form onSubmit={handleActiveCodeSubmit} className="w-full h-full">
                              <input
                                ref={codeInputRef}
                                type="text"
                                autoFocus
                                value={activeCodeInput}
                                onChange={e => setActiveCodeInput(e.target.value)}
                                onKeyDown={handleActiveInputKeyDown}
                                placeholder="Code..."
                                className="w-full h-full px-2 py-3 bg-transparent border-0 outline-none focus:ring-0 text-xs font-bold text-slate-900 placeholder:text-slate-400 thick-caret transition-colors"
                              />
                            </form>
                          </td>
                        ) : (
                          <td
                            className="px-2 py-3 border-r border-slate-200 align-middle cursor-pointer select-none text-slate-400 italic text-[11px] font-normal focus:bg-slate-50 focus:outline-none"
                            tabIndex={0}
                            onClick={() => {
                              focusTargetRef.current = 'code';
                              setIsEditingActiveRow(true);
                            }}
                            onFocus={() => {
                              focusTargetRef.current = 'code';
                              setIsEditingActiveRow(true);
                            }}
                          />
                        )
                      )}

                      {/* Item Name Cell */}
                      {isEditingActiveRow ? (
                        <td ref={searchCellRef} className="p-0 border-r border-slate-200 align-middle relative">
                          <div className="w-full h-full">
                            <form onSubmit={handleActiveCodeSubmit} className="w-full h-full">
                              <input
                                ref={nameInputRef}
                                type="text"
                                value={activeNameInput}
                                onChange={e => setActiveNameInput(e.target.value)}
                                onKeyDown={handleActiveInputKeyDown}
                                placeholder="Search Name..."
                                className="w-full h-full px-3 py-3 bg-transparent border-0 outline-none focus:ring-0 text-xs font-bold text-slate-900 placeholder:text-slate-400 thick-caret transition-colors"
                              />
                            </form>

                            {/* Search Dropdown Match List */}
                            {(activeMatches.length > 0 || activeNameInput.trim().length > 0) && (
                              <div ref={searchDropdownRef} className="fixed bg-white border border-slate-200 rounded-lg shadow-2xl z-[99999] overflow-hidden max-h-48 overflow-y-auto custom-scrollbar">
                                {activeMatches.length > 0 ? (
                                  activeMatches.map((item, index) => {
                                    const isHighlighted = index === focusedMatchIndex;
                                    return (
                                      <button
                                        key={item.stockRowId}
                                        type="button"
                                        onClick={() => {
                                          addToCart(item);
                                          setActiveCodeInput('');
                                          setActiveNameInput('');
                                          focusTargetRef.current = org.type === ORG_TYPES.SERVICE ? 'name' : 'code';
                                          setTimeout(() => (org.type === ORG_TYPES.SERVICE ? nameInputRef : codeInputRef).current?.focus(), 50);
                                        }}
                                        className="w-full text-left px-4 py-2.5 flex items-center justify-between border-b border-slate-100 last:border-b-0 cursor-pointer transition-colors hover:bg-slate-50"
                                        style={isHighlighted ? { backgroundColor: `${accent}15` } : {}}
                                      >
                                        <div className="min-w-0 flex-1">
                                          <p className="text-xs font-bold text-slate-800 truncate">
                                            {item.name}
                                          </p>
                                          {(() => {
                                            const inCart = cart.find(c => c.stockRowId === item.stockRowId)?.qty || 0;
                                            const rem = Math.max(0, item.qty - inCart);
                                            if (item.isService) return null;
                                            return <p className="text-[10px] text-slate-400 font-semibold mt-0.5">Code: {item.barcode || item.productId}{inventoryEnabled ? ` · ${rem} in stock` : ''}</p>;
                                          })()}
                                        </div>
                                        <div className="text-right ml-4 shrink-0 flex flex-col items-end">
                                          <p className="text-xs font-extrabold text-slate-700">₹{item.price.toFixed(2)}</p>
                                        </div>
                                      </button>
                                    );
                                  })
                                ) : (
                                  <div className="px-4 py-3 text-center text-xs font-semibold text-slate-400 italic">
                                    No items found.
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      ) : (
                        <td
                          className="px-3 py-3 border-r border-slate-200 align-middle cursor-pointer select-none text-slate-400 italic text-[11px] font-normal focus:bg-slate-50 focus:outline-none"
                          tabIndex={0}
                          onClick={() => {
                            focusTargetRef.current = 'name';
                            setIsEditingActiveRow(true);
                          }}
                          onFocus={() => {
                            focusTargetRef.current = 'name';
                            setIsEditingActiveRow(true);
                          }}
                        />
                      )}

                      {org.type !== ORG_TYPES.PRODUCT && (
                        <td ref={staffCellRef} className="p-0 border-r border-slate-200 align-middle relative">
                          <div className="w-full h-full relative">
                            <input
                              ref={staffInputRef}
                              type="text"
                              value={activeStaffInput}
                              onBlur={() => setTimeout(() => setIsStaffDropdownOpen(false), 200)}
                              onChange={(e) => {
                                setActiveStaffInput(e.target.value);
                                setActiveStaffId('');
                                setIsStaffDropdownOpen(true);
                              }}
                              placeholder={activeStaffId ? staffOptions.find(s => s.id === activeStaffId)?.name : "Search Staff..."}
                              className="w-full h-full px-2 py-3 bg-transparent border-0 outline-none focus:ring-0 text-xs font-bold text-slate-900 placeholder:text-slate-400 thick-caret transition-colors"
                            />
                            {isStaffDropdownOpen && activeStaffInput.trim().length > 0 && (
                              <div ref={staffDropdownRef} className="fixed bg-white border border-slate-200 rounded-lg shadow-2xl z-[99999] overflow-hidden max-h-48 overflow-y-auto custom-scrollbar">
                                {filteredStaffMatches.length > 0 ? (
                                  filteredStaffMatches.map((u) => (
                                    <button
                                      key={u.id}
                                      type="button"
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => {
                                        setActiveStaffId(u.id);
                                        setActiveStaffInput(u.name);
                                        setIsStaffDropdownOpen(false);
                                      }}
                                      className="w-full text-left px-4 py-2 flex items-center justify-between border-b border-slate-100 last:border-b-0 cursor-pointer transition-colors hover:bg-slate-50"
                                    >
                                      <p className="text-xs font-bold text-slate-800 truncate">{u.name}</p>
                                    </button>
                                  ))
                                ) : (
                                  <div className="px-4 py-3 text-center text-xs font-semibold text-slate-400 italic">
                                    No staff found.
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      )}

                      <td className="px-2 py-3 border-r border-slate-200" onClick={() => { focusTargetRef.current = org.type === ORG_TYPES.SERVICE ? 'name' : 'code'; setIsEditingActiveRow(true); }}>&nbsp;</td>
                      <td className="px-2 py-3 border-r border-slate-200" onClick={() => { focusTargetRef.current = org.type === ORG_TYPES.SERVICE ? 'name' : 'code'; setIsEditingActiveRow(true); }}>&nbsp;</td>
                      <td className="px-2 py-3 border-r border-slate-200" onClick={() => { focusTargetRef.current = org.type === ORG_TYPES.SERVICE ? 'name' : 'code'; setIsEditingActiveRow(true); }}>&nbsp;</td>
                      <td className="px-2 py-3 border-r border-slate-200" onClick={() => { focusTargetRef.current = org.type === ORG_TYPES.SERVICE ? 'name' : 'code'; setIsEditingActiveRow(true); }}>&nbsp;</td>
                      <td className="px-2 py-3 border-r border-slate-200" onClick={() => { focusTargetRef.current = org.type === ORG_TYPES.SERVICE ? 'name' : 'code'; setIsEditingActiveRow(true); }}>&nbsp;</td>
                      <td className="px-3 py-3 border-r border-slate-200" onClick={() => { focusTargetRef.current = org.type === ORG_TYPES.SERVICE ? 'name' : 'code'; setIsEditingActiveRow(true); }}>&nbsp;</td>
                      <td className="px-2 py-3" onClick={() => { focusTargetRef.current = org.type === ORG_TYPES.SERVICE ? 'name' : 'code'; setIsEditingActiveRow(true); }}>&nbsp;</td>
                    </tr>

                    {/* Faint Empty Placeholder Rows */}
                    {Array.from({ length: Math.max(0, 18 - cart.length - 1) }).map((_, i) => (
                      <tr key={`empty-${i}`} className="border-b border-slate-200 last:border-b-0">
                        <td className="px-2 py-3.5 border-r border-slate-200 text-center text-slate-300 font-semibold">&nbsp;</td>
                        {org.type !== ORG_TYPES.SERVICE && (
                          <td className="px-2 py-3.5 border-r border-slate-200">&nbsp;</td>
                        )}
                        <td className="px-3 py-3.5 border-r border-slate-200">&nbsp;</td>
                        {org.type !== ORG_TYPES.PRODUCT && (
                          <td className="px-2 py-3.5 border-r border-slate-200">&nbsp;</td>
                        )}
                        <td className="px-2 py-3.5 border-r border-slate-200">&nbsp;</td>
                        <td className="px-2 py-3.5 border-r border-slate-200">&nbsp;</td>
                        <td className="px-2 py-3.5 border-r border-slate-200">&nbsp;</td>
                        <td className="px-2 py-3.5 border-r border-slate-200">&nbsp;</td>
                        <td className="px-2 py-3.5 border-r border-slate-200">&nbsp;</td>
                        <td className="px-3 py-3.5 border-r border-slate-200">&nbsp;</td>
                        <td className="px-2 py-3.5">&nbsp;</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-[var(--theme-secondary)] border-t border-[var(--theme-tertiary)] px-4 py-3 flex flex-nowrap items-center justify-between gap-2 shadow-sm shrink-0">
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded border border-[var(--theme-tertiary)] shadow-sm shrink-0 whitespace-nowrap">
                  <span className="uppercase tracking-wider text-[10px] text-slate-400 font-semibold">CGST</span>
                  <span className="font-bold text-slate-700 text-[11px]">₹{totals.cgst.toFixed(2)}</span>
                  <div className="w-[1px] h-3 bg-slate-200 mx-1"></div>
                  <span className="uppercase tracking-wider text-[10px] text-slate-400 font-semibold">SGST</span>
                  <span className="font-bold text-slate-700 text-[11px]">₹{totals.sgst.toFixed(2)}</span>
                  <div className="w-[1px] h-3 bg-slate-200 mx-1"></div>
                  <span className="uppercase tracking-wider text-[10px] text-slate-400 font-semibold">IGST</span>
                  <span className="font-bold text-slate-700 text-[11px]">₹{totals.igst.toFixed(2)}</span>
                  {totals.deliveryFee > 0 && (
                    <>
                      <div className="w-[1px] h-3 bg-slate-200 mx-1"></div>
                      <span className="uppercase tracking-wider text-[10px] text-slate-400 font-semibold">DEL</span>
                      <span className="font-bold text-slate-700 text-[11px]">₹{totals.deliveryFee.toFixed(2)}</span>
                    </>
                  )}
                  {totals.discount > 0 && (
                    <>
                      <div className="w-[1px] h-3 bg-slate-200 mx-1"></div>
                      <span className="uppercase tracking-wider text-[10px] text-green-600 font-semibold">DISC</span>
                      <span className="font-bold text-green-600 text-[11px]">-₹{totals.discount.toFixed(2)}</span>
                    </>
                  )}
                </div>

                <div className="flex items-end gap-2 shrink-0 ml-auto">
                  {/* Table Layout Promo UI */}
                  {(isModuleOn(org, 'memberships') || isModuleOn(org, 'offers_discounts')) && (
                    <div className="flex flex-col gap-0.5 items-start">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Promo Code</span>
                      <div className="flex gap-1">
                        <input
                          type="text"
                          value={promoCodeInput}
                          onChange={e => setPromoCodeInput(e.target.value.toUpperCase())}
                          placeholder="Code"
                          className="w-[80px] h-[28px] rounded px-2 border border-[var(--theme-tertiary)] text-[11px] font-medium focus:outline-none uppercase"
                        />
                        <button
                          onClick={handleApplyPromo}
                          className="h-[28px] px-2 bg-[var(--theme-primary)] hover:opacity-90 text-white rounded font-bold uppercase tracking-wider text-[10px] shadow-sm transition-all"
                        >
                          Apply
                        </button>
                        {appliedDiscount && (
                          <button onClick={() => { setAppliedDiscount(null); setPromoCodeInput(''); }} className="h-[28px] px-2 bg-red-50 text-red-500 rounded font-bold uppercase tracking-wider text-[10px] hover:bg-red-100 transition-all">X</button>
                        )}
                      </div>
                    </div>
                  )}

                  {orderType === 'set-to-deliver' && (
                    <div className="flex items-end gap-1.5">
                      <div className="flex flex-col gap-0.5 items-start">
                        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Delivery Fee</span>
                        <CustomSelect
                          id="table-delivery-fee-bottom"
                          value={deliveryFeeSelected}
                          onChange={setDeliveryFeeSelected}
                          menuPlacement="top"
                          options={[
                            { value: 'none', label: 'No Fee' },
                            ...deliveryFeeSlabs.map(s => ({ value: String(s.id), label: `Up to ${s.upto} KM — ₹${Number(s.fee).toFixed(2)}` })),
                            { value: 'custom', label: 'Custom…' },
                          ]}
                          className="w-[100px] h-[28px] rounded text-[11px] font-medium normal-case tracking-normal px-2 focus:outline-none"
                        />
                      </div>
                      {deliveryFeeSelected === 'custom' && (
                        <div className="flex flex-col gap-0.5 items-start">
                          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 opacity-0">Fee</span>
                          <div className="relative">
                            <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-400">₹</span>
                            <input
                              type="text" inputMode="decimal"
                              value={customDeliveryFee}
                              onChange={e => {
                                const val = e.target.value;
                                if (/^\d*\.?\d*$/.test(val)) setCustomDeliveryFee(val);
                              }}
                              placeholder="0"
                              className="w-[64px] pl-4 pr-2 py-1 border border-[var(--theme-tertiary)] rounded bg-white text-[11px] font-bold text-slate-800 focus:border-[var(--theme-primary)] focus:outline-none shadow-sm h-[28px]"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {orderTypeOptions.length > 1 && (
                    <div className="flex flex-col gap-0.5 items-start">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Order Type</span>
                      <CustomSelect
                        id="table-order-type-bottom"
                        value={orderType}
                        onChange={setOrderType}
                        menuPlacement="top"
                        options={orderTypeOptions.map(o => ({ value: o, label: ORDER_TYPE_META[o].label }))}
                        className="w-[100px] h-[28px] rounded text-[11px] font-medium normal-case tracking-normal px-2 focus:outline-none"
                      />
                    </div>
                  )}

                  <div className="flex flex-col gap-0.5 items-start">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Payment</span>
                    <CustomSelect
                      id="table-payment-bottom"
                      value={paymentMethod}
                      onChange={setPaymentMethod}
                      menuPlacement="top"
                      options={[
                        { value: 'Cash', label: 'Cash' },
                        { value: 'Card', label: 'Card' },
                      ]}
                      className="w-[80px] h-[28px] rounded text-[11px] font-medium normal-case tracking-normal px-2 focus:outline-none"
                    />
                  </div>

                  <button
                    onClick={() => proceed(isPrinterModuleEnabled)}
                    className="h-[28px] px-4 bg-[var(--theme-primary)] hover:bg-[var(--theme-primary-dark)] text-white rounded font-bold uppercase tracking-wider text-[10px] shadow-sm transition-all flex items-center justify-center gap-2"
                  >
                    {isPrinterModuleEnabled ? 'Print' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>

        {layoutMode === 'card' && (
          <aside className={`w-full md:w-[320px] lg:w-[350px] shrink-0 min-h-0 bg-white rounded-lg border border-[var(--theme-tertiary)] shadow-sm flex-col overflow-hidden ${mobileView === 'receipt' ? 'flex' : 'hidden md:flex'}`}>
            <header className="shrink-0 px-5 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setMobileView('catalog')} className="md:hidden p-1.5 -ml-1.5 rounded-full text-slate-400 hover:text-[#0d0d0d] hover:bg-slate-50 transition-colors">
                  <ArrowRight size={16} className="rotate-180" />
                </button>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Bill</p>
                  <p className="text-sm font-bold text-[#0d0d0d]">Draft</p>
                </div>
              </div>

            </header>

            {orderTypeOptions.length > 1 && (
              <div className="shrink-0 px-5 pt-4">
                <div
                  role="tablist"
                  aria-label="Order type"
                  className={`grid bg-[var(--theme-secondary)] rounded-full p-1 text-xs font-semibold uppercase tracking-[0.14em] ${orderTypeOptions.length === 1 ? 'grid-cols-1' : (orderTypeOptions.length === 2 ? 'grid-cols-2' : 'grid-cols-3')}`}
                >
                  {orderTypeOptions.map((t) => {
                    const meta = ORDER_TYPE_META[t];
                    const Icon = meta.icon;
                    const active = orderType === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        title={meta.desc}
                        onClick={() => setOrderType(t)}
                        className="px-3 py-2 rounded-lg transition-all inline-flex items-center justify-center gap-1.5"
                        style={active ? { background: accent, color: '#fff' } : { color: '#64748b' }}
                      >
                        <Icon size={11} aria-hidden="true" /> {meta.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="shrink-0 px-5 pt-4 relative">
              <label className="block">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Customer name</span>
                <input
                  type="text"
                  value={customer}
                  onChange={(e) => setCustomer(e.target.value)}
                  placeholder="Walk-in"
                  className="w-full mt-1 rounded-lg border border-[var(--theme-tertiary)] bg-[var(--theme-secondary)] focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-slate-700"
                />
              </label>
              <label className="block mt-3">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Customer No.</span>
                <input
                  type="tel"
                  list="customer-phones"
                  value={customerPhone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    if (val.length <= 10) {
                      setCustomerPhone(val);

                      // Auto-detect customer
                      const matchedCust = customers.find(c => c.phone === val);
                      if (matchedCust) {
                        setCustomer(matchedCust.name);

                        // Auto-detect membership and apply discount
                        const activeMem = customerMemberships.find(m => (m.customerId || '').trim() === (matchedCust.id || '').trim() && m.status === 'active');
                        if (activeMem && activeMem.plan && activeMem.plan.discountPercent > 0) {
                          setAppliedDiscount({
                            id: activeMem.plan.id,
                            type: 'percentage',
                            value: activeMem.plan.discountPercent,
                            name: `👑 ${activeMem.plan.name} (${activeMem.plan.discountPercent}%)`,
                            code: 'MEMBERSHIP'
                          });
                        }
                      }
                    }
                  }}
                  placeholder="9999999999"
                  className="w-full mt-1 rounded-lg border border-[var(--theme-tertiary)] bg-[var(--theme-secondary)] focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-slate-700"
                />
                <datalist id="customer-phones">
                  {customers.map(c => (
                    <option key={c.id} value={c.phone}>{c.name}</option>
                  ))}
                </datalist>
              </label>
            </div>

            {orderType === 'set-to-deliver' && (
              <div className="shrink-0 px-5 pt-4">
                <label className="block">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 inline-flex items-center gap-1.5">
                    <Truck size={11} aria-hidden="true" /> Delivery address
                  </span>
                  <textarea
                    value={deliveryAddress}
                    onChange={(e) => setDeliveryAddress(e.target.value)}
                    placeholder="Street, area, landmark, pincode…"
                    rows={2}
                    className="w-full mt-1 rounded-lg border border-[var(--theme-tertiary)] bg-[var(--theme-secondary)] focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-slate-700 resize-none"
                  />
                </label>
              </div>
            )}

            <div className="shrink-0 px-5 pt-3 pb-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 inline-flex items-center gap-1.5"><ListChecks size={11} aria-hidden="true" /> Order list</p>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3 custom-scrollbar">
              {cart.length === 0 ? (
                <div key="empty"
                  className="py-10 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">
                  Tap a stock item to start
                </div>
              ) : cart.map((c) => {
                const gst = findGst(c.gstId);
                return (
                  <div key={c.stockRowId}
                    className="rounded-lg border border-[var(--theme-tertiary)] hover:border-[var(--theme-primary-tint)] transition-colors p-3 mb-2 bg-white">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'var(--theme-secondary)', color: accent }}><Box size={18} strokeWidth={1.5} /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <h6 className="text-xs font-bold text-[#0d0d0d] truncate" title={`${c.name}${c.label && c.label !== c.name ? ` - ${c.label}` : ''}`}>
                              {c.name}
                              {c.label && c.label !== c.name && (
                                <span className="font-medium text-slate-500 ml-1">
                                  - {c.label}
                                </span>
                              )}
                            </h6>
                          </div>
                          <span className="text-xs font-bold text-[#0d0d0d]">₹{(c.price * c.qty).toFixed(2)}</span>
                        </div>
                        <p className="text-[11px] font-medium text-slate-400 mt-0.5 truncate">
                          {c.label} · ${c.price.toFixed(2)}
                          {gst
                            ? ` · ${isInclusive(taxModeOf(findCatalogProduct(c.productId) || c, shopTaxMode())) ? 'incl. ' : ''}GST ${gst.rate}%`
                            : ''}
                        </p>
                        <div className="mt-2 flex items-center justify-between">
                          <div className="inline-flex items-center gap-1 bg-[var(--theme-secondary)] rounded-lg px-1 py-0.5">
                            <button type="button" aria-label={`Decrease ${c.name}`} onClick={() => dec(c.stockRowId)} className="w-6 h-6 rounded-full text-slate-500 hover:bg-white flex items-center justify-center"><Minus size={12} /></button>
                            <span className="px-2 text-[11px] font-bold text-[#0d0d0d] min-w-[18px] text-center">{c.qty}</span>
                            <button type="button" aria-label={`Increase ${c.name}`} onClick={() => inc(c.stockRowId)} className="w-6 h-6 rounded-full text-slate-500 hover:bg-white flex items-center justify-center"><Plus size={12} /></button>
                          </div>
                          <button type="button" aria-label={`Remove ${c.name}`} onClick={() => drop(c.stockRowId)} className="p-1.5 rounded-md text-slate-400 hover:text-rose-500 hover:bg-rose-50"><Trash2 size={13} /></button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="shrink-0 px-5 pt-3 pb-3 border-t border-[var(--theme-tertiary)]">
              <div className="grid grid-cols-2 gap-2 mb-3">
                {['Cash', 'Card'].map((m) => (
                  <button key={m} type="button" onClick={() => setPaymentMethod(m)}
                    className="px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-[0.14em] transition-all border"
                    style={paymentMethod === m
                      ? { borderColor: accent, background: `${accent}11`, color: accent }
                      : { borderColor: 'var(--theme-tertiary)', color: '#64748b' }}>
                    {m}
                  </button>
                ))}
              </div>

              {(isModuleOn(org, 'memberships') || isModuleOn(org, 'offers_discounts')) && (
                <div className="pt-3 border-t border-slate-100 px-1 mt-auto">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 inline-flex items-center gap-1 mb-1"><Tag size={10} />Promo Code</span>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCodeInput}
                      onChange={e => setPromoCodeInput(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      className="flex-1 px-3 py-1.5 border border-[var(--theme-tertiary)] rounded-lg bg-white text-xs font-bold text-slate-800 focus:border-[var(--theme-primary)] focus:outline-none uppercase"
                    />
                    <button
                      type="button"
                      onClick={handleApplyPromo}
                      className="px-3 py-1.5 bg-[var(--theme-primary)] text-white text-xs font-semibold rounded-lg hover:opacity-90 transition-all"
                    >
                      Apply
                    </button>
                  </div>
                  {appliedDiscount && (
                    <div className="mt-1.5 flex items-center justify-between px-1">
                      <span className="text-[10px] font-medium text-emerald-600">"{appliedDiscount.code}" applied</span>
                      <button type="button" onClick={() => { setAppliedDiscount(null); setPromoCodeInput(''); }} className="text-red-500 hover:underline hover:text-red-700 transition-colors">Remove</button>
                    </div>
                  )}
                </div>
              )}

              {/* Delivery Fee Selector — Card sidebar */}
              {orderType === 'set-to-deliver' && (
                <div className="mb-3">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 inline-flex items-center gap-1 mb-1"><Truck size={10} />Delivery Fee</span>
                  <div className="bg-white rounded-lg border border-[var(--theme-tertiary)] shadow-sm">
                    <CustomSelect
                      id="card-delivery-fee"
                      value={deliveryFeeSelected}
                      onChange={setDeliveryFeeSelected}
                      menuPlacement="top"
                      options={[
                        { value: 'none', label: 'No Fee' },
                        ...deliveryFeeSlabs.map(s => ({ value: String(s.id), label: `Up to ${s.upto} KM — ₹${Number(s.fee).toFixed(2)}` })),
                        { value: 'custom', label: 'Custom…' },
                      ]}
                      className="w-full border-none bg-transparent text-xs font-medium normal-case tracking-normal py-1.5 px-3 focus:outline-none"
                    />
                  </div>
                  {deliveryFeeSelected === 'custom' && (
                    <div className="relative mt-2">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">₹</span>
                      <input
                        type="number" min="0" step="0.5"
                        value={customDeliveryFee}
                        onChange={e => setCustomDeliveryFee(e.target.value)}
                        placeholder="Enter amount"
                        className="w-full pl-6 pr-3 py-1.5 border border-[var(--theme-tertiary)] rounded-lg bg-white text-xs font-bold text-slate-800 focus:border-[var(--theme-primary)] focus:outline-none"
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-1.5 text-[11px] font-medium text-slate-500">
                <div className="flex items-center justify-between"><span>Subtotal</span><span className="font-bold text-[#0d0d0d]">₹{totals.subtotal.toFixed(2)}</span></div>
                {totals.discount > 0 && (
                  <div className="flex items-center justify-between"><span className="text-green-600">Discount</span><span className="font-bold text-green-600">-₹{totals.discount.toFixed(2)}</span></div>
                )}
                <div className="flex items-center justify-between"><span className="inline-flex items-center gap-1"><Percent size={10} aria-hidden="true" /> CGST</span><span className="font-bold text-[#0d0d0d]">₹{totals.cgst.toFixed(2)}</span></div>
                <div className="flex items-center justify-between"><span className="inline-flex items-center gap-1"><Percent size={10} aria-hidden="true" /> SGST</span><span className="font-bold text-[#0d0d0d]">₹{totals.sgst.toFixed(2)}</span></div>
                {totals.deliveryFee > 0 && (
                  <div className="flex items-center justify-between"><span className="inline-flex items-center gap-1"><Truck size={10} aria-hidden="true" /> Delivery</span><span className="font-bold text-[#0d0d0d]">₹{totals.deliveryFee.toFixed(2)}</span></div>
                )}
                {/* Only when there is something to say. A shop whose prices
                    come to whole rupees would otherwise read "Round off
                    ₹0.00" on every sale it ever made. */}
                {totals.roundOff !== 0 && (
                  <div className="flex items-center justify-between"><span>Round off</span><span className="font-bold text-[#0d0d0d]">{totals.roundOff > 0 ? '+' : '−'}₹{Math.abs(totals.roundOff).toFixed(2)}</span></div>
                )}
                <div className="flex items-center justify-between pt-2 mt-1 border-t border-[var(--theme-tertiary)]">
                  <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-700">Total</span>
                  <span className="text-base font-bold" style={{ color: accent }}>₹{totals.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <button type="button" onClick={() => proceed(isPrinterModuleEnabled)} aria-label={isPrinterModuleEnabled ? "Proceed and print" : "Proceed and save"}
              className="shrink-0 rounded-lg px-4 py-2 font-bold text-xs uppercase tracking-wider transition-all text-white flex items-center gap-2 m-5 mt-auto ml-auto"
              style={{ background: accent }}>
              {isPrinterModuleEnabled ? 'Proceed & print' : 'Save Bill'}
            </button>
          </aside>
        )}
      </div>

      {showWifiModal && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <Wifi size={18} className="text-blue-600" />
                Connect Wi-Fi Printer
              </h2>
              <button
                onClick={() => setShowWifiModal(false)}
                disabled={isTestingWifi}
                className="text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-5">
              <div className="text-sm text-slate-600 mb-5 space-y-2">
                <p className="font-semibold text-slate-700">How to connect:</p>
                <ol className="list-decimal pl-4 space-y-1 text-slate-500">
                  <li>Ensure this device is on the <strong>same Wi-Fi network</strong> as the printer.</li>
                  <li>Print the <strong>self-test page</strong> on your printer to find its IP address.</li>
                  <li>Type the IP address below.</li>
                </ol>
              </div>

              {wifiTestError && (
                <div className="mb-3 p-2 bg-rose-50 text-rose-600 text-[11px] font-semibold rounded-md border border-rose-100">
                  {wifiTestError}
                </div>
              )}

              <input
                type="text"
                value={wifiIpInput}
                onChange={(e) => {
                  setWifiIpInput(e.target.value);
                  setWifiTestError('');
                }}
                disabled={isTestingWifi}
                placeholder="e.g. 192.168.1.100"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-400"
                autoFocus
                onKeyDown={async (e) => {
                  if (e.key === 'Enter') {
                    if (!wifiIpInput || isTestingWifi) return;

                    setIsTestingWifi(true);
                    setWifiTestError('');
                    try {
                      const res = await fetchWithAuth(`/print/wifi/check?ip=${wifiIpInput}`);
                      const resData = await res.json().catch(() => ({}));
                      if (!res.ok) throw new Error(resData.error || 'Connection failed.');

                      localStorage.setItem('printerWifiIp', wifiIpInput);
                      localStorage.setItem('org.device.printer', JSON.stringify({ method: 'wifi', ip: wifiIpInput }));
                      setShowWifiModal(false);
                      if (showNotification) showNotification('Wi-Fi printer connected!', 'success');
                    } catch (err) {
                      setWifiTestError(err.message || 'Could not connect to printer.');
                    } finally {
                      setIsTestingWifi(false);
                    }
                  }
                }}
              />
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-4 bg-slate-50 border-t border-slate-100">
              <button
                onClick={() => setShowWifiModal(false)}
                disabled={isTestingWifi}
                className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-800 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!wifiIpInput) return;

                  setIsTestingWifi(true);
                  setWifiTestError('');
                  try {
                    const res = await fetchWithAuth(`/print/wifi/check?ip=${wifiIpInput}`);
                    const resData = await res.json().catch(() => ({}));
                    if (!res.ok) throw new Error(resData.error || 'Connection failed.');

                    localStorage.setItem('printerWifiIp', wifiIpInput);
                    localStorage.setItem('org.device.printer', JSON.stringify({ method: 'wifi', ip: wifiIpInput }));
                    setShowWifiModal(false);
                    if (showNotification) showNotification('Wi-Fi printer connected!', 'success');
                  } catch (err) {
                    setWifiTestError(err.message || 'Could not connect to printer.');
                  } finally {
                    setIsTestingWifi(false);
                  }
                }}
                disabled={!wifiIpInput || isTestingWifi}
                className="px-4 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 min-w-[100px]"
              >
                {isTestingWifi ? 'Testing...' : 'Connect'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

BillingTab.propTypes = {
  org: PropTypes.object.isRequired,
  user: PropTypes.object,
  showNotification: PropTypes.func,
  onBillPrinted: PropTypes.func,
  lockedBranchId: PropTypes.string,
};
